import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Mail, Send } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

export function TestEmailForm() {
  const [emailType, setEmailType] = useState("report");
  const [isSending, setIsSending] = useState(false);
  const { toast } = useToast();

  const sendTestEmail = async () => {
    setIsSending(true);
    
    try {
      const res = await apiRequest(
        "POST", 
        "/api/test-email", 
        { emailType }
      );
      
      const data = await res.json();
      
      if (res.ok) {
        toast({
          title: "Test Email Sent",
          description: "Please check your email inbox.",
        });
      } else {
        throw new Error(data.message || "Failed to send test email");
      }
    } catch (error: any) {
      toast({
        title: "Error Sending Test Email",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Mail className="h-5 w-5 mr-2" />
          Test Email Delivery
        </CardTitle>
        <CardDescription>
          Send a test email to verify your notification settings
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium" htmlFor="emailType">
              Email Type
            </label>
            <Select 
              defaultValue="report" 
              onValueChange={setEmailType}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="report">Financial Report</SelectItem>
                <SelectItem value="task">Task Reminder</SelectItem>
                <SelectItem value="subscription">Subscription Alert</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="text-sm text-neutral-500">
            <p>
              The test email will be sent to the email address associated with your account.
              This helps ensure that you can receive notifications correctly.
            </p>
          </div>
        </div>
      </CardContent>
      <CardFooter>
        <Button 
          onClick={sendTestEmail} 
          disabled={isSending}
          className="w-full"
        >
          {isSending ? (
            <>
              <span className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></span>
              Sending...
            </>
          ) : (
            <>
              <Send className="h-4 w-4 mr-2" />
              Send Test Email
            </>
          )}
        </Button>
      </CardFooter>
    </Card>
  );
}